import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SELECTOR_TYPE, updateSelectorType } from "../../reducers/period";
import { PageTitleSection } from "../../common/components/PageTitleSection/PageTitleSection";
import TEALIUM from "../../common/enums/tealium-enums";
import defaultContent from "../../content/frontlineQ";
import commonContent from "../../content/common";
import { getCanonicalTitle } from "../../common/util/Incentives/IncentiveRegistry";
import { useAppDispatch, useAppSelector } from "../../store";
import { QUALIFICATION_STATUS } from "../../common/enums/incentive-enums";
import { BonusInfoFooter } from "../../common/components/BonusInfoFooter/BonusInfoFooter";
import { IncentiveCalculation } from "../../common/components/IncentiveCalculation/IncentiveCalculation";
import { IconMultiply } from "../../common/icons/IconMultiply/IconMultiply";
import { IconEquals } from "../../common/icons/IconEquals/IconEquals";
import PerformanceTrackingTile from "../../common/components/PerformanceTrackingTile/PerformanceTrackingTile";
import { AnnualVolumeRequirementTile } from "../../common/components/RequirementTiles/AnnualVolumeRequirementTile/AnnualVolumeRequirementTile";
import { AWARD_CODES } from "../../common/enums/award-codes";
import { getIncentiveViewID } from "../../common/util/IncentivesViewID/IncentiveViewID";
import { ErrorPage, ERROR_TYPE } from "../ErrorPage/ErrorPage";
import { BadgeRequirementTileV2 } from "../../common/components/RequirementTiles/BadgeRequirementTileV2/BadgeRequirementTileV2";
import { getPrevYearShort, getSelectedPerformanceYearData } from "../../common/util/period";
import LeafTile from "../../common/leaf/LeafTile/LeafTile";
import { ErrorTile } from "../../common/components/ErrorTile/ErrorTile";
import { FrontlineQBannerTile } from "./FrontlineQBannerTile/FrontlineQBannerTile";
import PaymentUnitDetailsTile from "./PaymentUnitDetails/PaymentUnitDetails";
import { FetchFrontlineQOverviewData } from "./FrontlineQDataLayer";
import "./FrontlineQ.scss";
import {
  hideButtonAndSkipRequest,
  setModalBtnFocus,
} from "../FrontlineGroupGrowth/MultiplierDetails/MultiplierDetailsUtil";
import { CurrentFQModal } from "../../common/components/Modals/CurrentFQModal/CurrentFQModal";
import { FetchMultiplerDetailsData } from "../FrontlineGroupGrowth/MultiplierDetails/MultiplierDetailsDataLayer";
import { PreviousFQModal } from "../FrontlineGroupGrowth/PreviousPYorBaselineModal/PreviousFQ/PreviousFQModal/PreviousFQModal";
import { IconAlert } from "../../common/icons/IconAlert/IconAlert";
import { track } from "../../common/util/tealium";

export default function FrontlineQ() {
  const { t } = useTranslation(["frontlineQ", "navigation", "common"]);
  const { configuration, shellConfig } = useAppSelector((state) => state.boot);
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const dispatch = useAppDispatch();
  const { isComingSoon = false } = shellConfig;
  const learnMoreLink = configuration.dashboardLinks?.learnMore?.frontlineGrowth ?? "";
  const { selectedPYEndPeriod: previousPyEndPeriod } = getSelectedPerformanceYearData(selectedPeriod, 1);
  const currentFqModalButton = useRef<HTMLButtonElement>(null);
  const prevOpenRef = useRef(false);
  const [showCurrentFqModal, setShowCurrentFqModal] = useState(false);
  const prevFqModalButton = useRef<HTMLButtonElement>(null);
  const [showPrevFqModal, setShowPrevFqModal] = useState(false);

  useEffect(() => {
    dispatch(updateSelectorType(SELECTOR_TYPE.PY));
  }, [dispatch]);

  useEffect(() => {
    track(TEALIUM.U_TAG_METHOD.VIEW, "", "Frontline_Q", "Frontline_Q", `${selectedPeriod}`, "");
  }, [selectedPeriod]);

  const handleTealiumTrackClickActionLearnMore = () => {
    track(
      TEALIUM.U_TAG_METHOD.LINK,
      TEALIUM.EVENT_NAME.CLICK_ACTION,
      "",
      TEALIUM.CATEGORY.Frontline_Q,
      "frontline_q",
      "learn_more",
    );
  };

  const titleContentKey = getIncentiveViewID(
    AWARD_CODES.FGG,
    selectedPeriod,
    configuration,
    shellConfig.aboInfo.isoCountryCode,
  );

  const {
    frontlineQOverviewData,
    isLoading: overviewLoading,
    errorStatus: overviewError,
    refetch: refetchFrontlineQOverviewData,
  } = FetchFrontlineQOverviewData(selectedPeriod, isComingSoon);

  const skipRequest = frontlineQOverviewData?.Requirements?.FQRequirement
    ? hideButtonAndSkipRequest(frontlineQOverviewData.Requirements.FQRequirement.BaselineFQs, selectedPeriod)
    : true;

  const currentSkipRequest = frontlineQOverviewData?.Requirements?.FQRequirement
    ? !frontlineQOverviewData.Requirements.FQRequirement.CurrentFQs ||
      frontlineQOverviewData.Requirements.FQRequirement.CurrentFQs === "0"
    : true;

  const {
    multiplierDetailsData: prevMultiplierData,
    isLoading: prevLoading,
    errorStatus: prevError,
    refetch: refetchPrev,
  } = FetchMultiplerDetailsData(previousPyEndPeriod, skipRequest);

  const {
    multiplierDetailsData: currentMultiplierData,
    isLoading: currentLoading,
    errorStatus: currentError,
    refetch: refetchCurrent,
  } = FetchMultiplerDetailsData(selectedPeriod, currentSkipRequest);

  const symbol = String(t("disclaimerSymbol", { defaultValue: commonContent.disclaimerSymbol, ns: "common" }));
  const pageTitle = getCanonicalTitle({ id: "frontlineQ" }, t, { symbol });

  if (overviewError) {
    return (
      <LeafTile hideBorder={true}>
        <ErrorTile
          clickFunction={refetchFrontlineQOverviewData}
          errorStatus={overviewError}
          isLoading={overviewLoading}
          tealiumTrackId="FrontlineQ_Overview"
        />
      </LeafTile>
    );
  }

  if (!overviewLoading && titleContentKey !== "frontlineQ") {
    return (
      <ErrorPage
        errorType={ERROR_TYPE.INCENTIVE_UNAVAILABLE_FOR_PERIOD}
        pageTitle={pageTitle}
        containerClassName="Frontlineq"
      />
    );
  }

  const createModalToggle =
    (
      showModal: boolean,
      setShowModal: (show: boolean) => void,
      buttonRef: React.RefObject<HTMLButtonElement | null>,
      trackingLabel: string,
    ) =>
    () => {
      setShowModal(!showModal);
      setModalBtnFocus(buttonRef, prevOpenRef, showModal);
      track(
        TEALIUM.U_TAG_METHOD.LINK,
        TEALIUM.EVENT_NAME.CLICK_ACTION,
        "",
        TEALIUM.CATEGORY.Frontline_Q,
        `${trackingLabel}_${!showModal ? "open" : "close"}`,
        "view_details_modal",
      );
    };

  const previousFqModalToggle = createModalToggle(
    showPrevFqModal,
    setShowPrevFqModal,
    prevFqModalButton,
    "previous_PY_FQ",
  );

  const currentFqModalToggle = createModalToggle(
    showCurrentFqModal,
    setShowCurrentFqModal,
    currentFqModalButton,
    "current_FQ",
  );

  const getClassNameForTile = (baseline: string, tracking: string): string => {
    const base = Number(baseline);
    const track = Number(tracking);

    if (base === 0 && track === 0) return "";
    return track >= base ? "green" : "yellow";
  };

  const getIcon = (color: string) => (color === "yellow" ? <IconAlert /> : null);

  const displayBanner = (status: QUALIFICATION_STATUS): boolean => {
    return (
      status === QUALIFICATION_STATUS.MANUALLY_APPROVED ||
      status === QUALIFICATION_STATUS.HELD ||
      status === QUALIFICATION_STATUS.DENIED
    );
  };

  return (
    <main className="Frontlineq">
      <PageTitleSection
        title={pageTitle}
        learnMoreLink={learnMoreLink}
        learnMoreTealiumFunction={handleTealiumTrackClickActionLearnMore}
      />
      <div className="Fgg__container-split">
        {displayBanner(frontlineQOverviewData.IncomeData.Status) || isComingSoon ? (
          <div className="Fgg__section-padding Fgg__container-row">
            <section className="Fgg__banner">
              <FrontlineQBannerTile
                incomeData={frontlineQOverviewData.IncomeData}
                isLoading={overviewLoading}
                isNewPerformanceYear={isComingSoon}
              />
            </section>
          </div>
        ) : (
          <div className="Frontlineq__container">
            <div className="Frontlineq__requirements">
              <h2>{t("requirements.sectionHeader", defaultContent.requirements.sectionHeader).toString()}</h2>
              <div className="Frontlineq__requirements-content">
                <div className="Frontlineq__longReq">
                  <AnnualVolumeRequirementTile
                    isLoading={overviewLoading}
                    currentAmount={{
                      actual: frontlineQOverviewData.Requirements.AnnualPPVRequirement.Volume.Current.Actual,
                      formatted: frontlineQOverviewData.Requirements.AnnualPPVRequirement.Volume.Current.Formatted,
                    }}
                    targetAmount={{
                      actual: frontlineQOverviewData.Requirements.AnnualPPVRequirement.Volume.Target.Actual,
                      formatted: frontlineQOverviewData.Requirements.AnnualPPVRequirement.Volume.Target.Formatted,
                    }}
                    met={frontlineQOverviewData.Requirements.AnnualPPVRequirement.Met}
                    volumeType="personal"
                    exempt={frontlineQOverviewData.Requirements.AnnualPPVRequirement.Exempt}
                  />
                </div>

                <BadgeRequirementTileV2
                  title={`${t(
                    "requirements.badgeRequirement.title",
                    defaultContent["requirements"]["badgeRequirement"]["title"],
                  )}`}
                  status={frontlineQOverviewData.Requirements.FoundersPlatinumRequirement.Status}
                  enableGarTracking={true}
                  hideBorder={false}
                  circleCheckmarkId={frontlineQOverviewData.Requirements.FoundersPlatinumRequirement.Name}
                  show={true}
                  isLoading={overviewLoading}
                />

                <PerformanceTrackingTile
                  met={frontlineQOverviewData.Requirements.FQRequirement.Met}
                  isLoading={overviewLoading}
                  title={t(
                    "requirements.q1qvRequirement.title",
                    defaultContent.requirements.q1qvRequirement.title,
                  ).toString()}
                  sections={[
                    {
                      title: t(
                        "requirements.q1qvRequirement.section1Title",
                        defaultContent.requirements.q1qvRequirement.section1Title,
                        { pvVal: getPrevYearShort(selectedPeriod) },
                      ).toString(),
                      value: frontlineQOverviewData.Requirements.FQRequirement.BaselineFQs,
                      showIcon: +frontlineQOverviewData.Requirements.FQRequirement.BaselineFQs > 0,
                      handleIconTableClick: previousFqModalToggle,
                    },
                    {
                      title: t(
                        "requirements.q1qvRequirement.section2Title",
                        defaultContent.requirements.q1qvRequirement.section2Title,
                      ).toString(),
                      value: frontlineQOverviewData.Requirements.FQRequirement.CurrentFQs,
                      showIcon: +frontlineQOverviewData.Requirements.FQRequirement.CurrentFQs > 0,
                      handleIconTableClick: currentFqModalToggle,
                    },
                    {
                      title: t(
                        "requirements.q1qvRequirement.section3Title",
                        defaultContent.requirements.q1qvRequirement.section3Title,
                      ).toString(),
                      value: (
                        <>
                          {getIcon(
                            getClassNameForTile(
                              frontlineQOverviewData.Requirements.FQRequirement.BaselineFQs,
                              frontlineQOverviewData.Requirements.FQRequirement.TrackingFQs,
                            ),
                          )}
                          {frontlineQOverviewData.Requirements.FQRequirement.TrackingFQs}
                        </>
                      ),
                      className: getClassNameForTile(
                        frontlineQOverviewData.Requirements.FQRequirement.BaselineFQs,
                        frontlineQOverviewData.Requirements.FQRequirement.TrackingFQs,
                      ),
                    },
                  ]}
                />
              </div>
            </div>

            <div className="Frontlineq__requirements">
              <h2>{t("paymentDetails.sectionTitle", defaultContent.paymentDetails.sectionTitle).toString()}</h2>
              <h3 className="Frontlineq__subheader">
                {t("paymentDetails.tableTitle", defaultContent.paymentDetails.tableTitle as string)}
              </h3>
              <PaymentUnitDetailsTile data={frontlineQOverviewData?.PaymentUnitDetails} />
            </div>

            <div>
              <h3 className="Frontlineq__subheader">
                {t("paymentDetails.incentiveCalc.title", defaultContent.paymentDetails.incentiveCalc.title).toString()}
              </h3>
            </div>

            <div className="Frontlineq__multiplication">
              <IncentiveCalculation
                isLoading={overviewLoading}
                base={{
                  title: t(
                    "paymentDetails.incentiveCalc.trackingFrontlineQs",
                    defaultContent.paymentDetails.incentiveCalc.trackingFrontlineQs,
                  ).toString(),
                  amount: `${frontlineQOverviewData.IncentiveCalculation.TrackingFQs} FQs`,
                }}
                multiplier={{
                  title: t(
                    "paymentDetails.incentiveCalc.paymentUnit",
                    defaultContent.paymentDetails.incentiveCalc.paymentUnit,
                  ).toString(),
                  amount: frontlineQOverviewData.IncentiveCalculation.PaymentUnit,
                  symbolIcon: <IconMultiply />,
                }}
                total={{
                  title: t(
                    "paymentDetails.incentiveCalc.potentialEarnings",
                    defaultContent.paymentDetails.incentiveCalc.potentialEarnings,
                  ).toString(),
                  amount: frontlineQOverviewData.IncentiveCalculation.PotentialPYEarnings,
                  symbolIcon: <IconEquals />,
                }}
              />
            </div>
          </div>
        )}
      </div>

      <CurrentFQModal
        currentFQLegs={currentMultiplierData.FqLegs}
        currentFQs={frontlineQOverviewData.Requirements.FQRequirement.CurrentFQs}
        isLoading={currentLoading}
        errorStatus={currentError}
        refetch={refetchCurrent}
        toggleModal={currentFqModalToggle}
        displayModal={showCurrentFqModal}
      />
      <PreviousFQModal
        previousFqLegs={prevMultiplierData.FqLegs}
        previousFQs={frontlineQOverviewData.Requirements.FQRequirement.BaselineFQs}
        isLoading={prevLoading}
        errorStatus={prevError}
        refetch={refetchPrev}
        toggleModal={previousFqModalToggle}
        displayModal={showPrevFqModal}
      />
      <BonusInfoFooter />
    </main>
  );
}
